import { useConfigContext } from '../contexts/ConfigContext';

/**
 * Custom hook for accessing application configuration
 * Provides a clean interface to configuration data with helpful utilities
 */
export const useConfig = () => {
  const {
    config,
    loading,
    error,
    retry,
    appName,
    appVersion,
    appShortDescription,
    appLongDescription,
    appCompanyDescription,
    appUserInstructions,
    googleClientId,
    personalities,
    categories,
    showPersonality,
    showCategory,
    showPhotoControl
  } = useConfigContext();

  return {
    // Raw configuration object
    config,
    
    // Loading and error states
    loading,
    error,
    retry,
    
    // Convenience accessors for app configuration
    app: {
      name: appName,
      version: appVersion,
      shortDescription: appShortDescription,
      longDescription: appLongDescription,
      companyDescription: appCompanyDescription
    },
    
    // Other configuration values
    googleClientId,
    personalities,
    categories,
    
    // UI visibility controls
    showPersonality,
    showCategory,
    showPhotoControl,
    
    // Utility functions
    isLoaded: !loading && !error,
    hasError: !!error,
    
    // Helper to get app title with fallback
    getAppTitle: (fallback = 'Loading...') => {
      if (loading) return fallback;
      return appName;
    },
    
    // Helper to get app description with fallback
    getAppDescription: (type = 'short', fallback = 'Loading...') => {
      if (loading) return fallback;
      return type === 'short' ? appShortDescription : appLongDescription;
    },
    
    // Helper to get company description with fallback
    getCompanyDescription: (fallback = 'Loading...') => {
      if (loading) return fallback;
      return appCompanyDescription;
    },
    
    // Helper to get user instructions with fallback
    getUserInstructions: (fallback = '') => {
      if (loading) return fallback;
      return appUserInstructions || '';
    },
    
    // Helper to check if configuration is ready for use
    isReady: () => !loading && !error && config && !!config.app
  };
};

export default useConfig;
